import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Employee from 'src/app/model/product/employee';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import {
  GroupSelectedEvent,
  LastSelectedPlanValues,
  ItemGroup,
  ImmoMapHelper,
  ItemPlannedEvent,
} from '../helper/immo-map-helper';
import {
  ImmoMapImmoTask,
  ImmoMapItem,
  ImmoMapSubProject,
} from '../services/model/immo-map-sub-project';
import { ImmoMapPopupInfoComponent } from './immo-map-popup-info/immo-map-popup-info.component';

@Component({
  selector: 'immo-map-popup',
  templateUrl: './immo-map-popup.component.html',
  styleUrls: ['./immo-map-popup.component.scss'],
})
export class ImmoMapPopupComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input()
  itemGroup: ItemGroup;
  @Input()
  placerOptions: Employee[];
  @Input()
  groupSelected: Subject<GroupSelectedEvent>;
  @Input()
  itemAddedToGroup: Observable<ItemGroup>;

  @Output()
  updatePlanValues = new EventEmitter<LastSelectedPlanValues>();
  @Output()
  lastItemPlanned = new EventEmitter<any>();
  @Output()
  jobClicked = new EventEmitter<any>();

  toggleDisplay = new Subject<number>();

  @ViewChildren(ImmoMapPopupInfoComponent)
  children: QueryList<ImmoMapPopupInfoComponent>;

  init: boolean = false;

  displayListClass: string = ImmoMapPopupComponent.SHOW_LIST_CLASS;
  alwaysDisplay: boolean = false;

  private static readonly SHOW_LIST_CLASS: string = '';
  private static readonly HIDE_LIST_CLASS: string = 'hidden';

  constructor(private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    this.calculateDisplayBooleans();
    this.preprocessItems();
    this.init = true;
  }

  ngAfterViewInit(): void {
    if (this.groupSelected != null) {
      this.addSubscription(this.groupSelected, (event: GroupSelectedEvent) => {
        if (
          event.lastSelectedPlanValues != null &&
          event.group === this.itemGroup
        ) {
          this.children.forEach((c) =>
            c.fillPlanValues(event.lastSelectedPlanValues)
          );
        }
      });
      this.cd.detectChanges();
    }

    if (this.itemAddedToGroup != null) {
      this.addSubscription(this.itemAddedToGroup, (group: ItemGroup) => {
        if (group != null && group === this.itemGroup) {
          this.calculateDisplayBooleans();
          this.preprocessItems();
          if (this.itemGroup.items.length > 1) {
            this.children.forEach((c) => c.doHide());
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.toggleDisplay.complete();
  }

  displayChild(index: number): void {
    this.displayListClass = ImmoMapPopupComponent.HIDE_LIST_CLASS;
    this.toggleDisplay.next(index);
    this.jobClicked.next();
  }

  backPressed(): void {
    this.displayListClass = ImmoMapPopupComponent.SHOW_LIST_CLASS;
  }

  private calculateDisplayBooleans(): void {
    this.alwaysDisplay = this.itemGroup.items.length === 1;
    this.displayListClass = this.alwaysDisplay
      ? ImmoMapPopupComponent.HIDE_LIST_CLASS
      : ImmoMapPopupComponent.SHOW_LIST_CLASS;
  }

  private preprocessItems(): void {
    this.itemGroup.items.forEach((sp) => {
      this.calculatePopupDisplay(sp);
    });
  }

  private calculatePopupDisplay(sp: ImmoMapItem): void {
    const displayProductList =
      sp.hasOwnProperty('project') &&
      (sp as ImmoMapSubProject).project != null &&
      CollectionHelper.isPopulated((sp as ImmoMapSubProject).project.products)
        ? StringHelper.join(
            (sp as ImmoMapSubProject).project.products.map((p) =>
              ProductHelper.isIPBoard(p)
                ? StringHelper.shortenText(
                    IPBoardHelper.getShortDescription(<IPBoard>p),
                    30
                  )
                : null
            ),
            ', '
          )
        : (sp as ImmoMapImmoTask).subject;
    const displayDate = sp.currentRequestASAP
      ? 'ASAP'
      : sp.currentRequestDate == null
      ? ''
      : DateHelper.formatDate(sp.currentRequestDate);
    sp.popupDisplay = StringHelper.join(
      [
        sp.hasOwnProperty('project') &&
        (sp as ImmoMapSubProject).project != null
          ? `#${(sp as ImmoMapSubProject).project.id.toString()}`
          : `#${sp.id.toString()}`,
        displayProductList,
        displayDate,
      ],
      ' - '
    );
  }
  placerUpdated(event: ItemPlannedEvent): void {
    this.updatePlanValues.emit(event.lastSelectedPlanValues);
  }
  itemPlanned(event: ItemPlannedEvent): void {
    this.updatePlanValues.emit(event.lastSelectedPlanValues);
    if (this.itemGroup.items.length === 1) {
      this.itemGroup.hide = true;
      this.itemGroup.items.length = 0;
      ImmoMapHelper.reprocessGroupAppointmentInfo(this.itemGroup);
      this.lastItemPlanned.emit();
    } else {
      this.backPressed();
      this.children.forEach((c) => {
        c.doHide();
        c.fillPlanValues(event.lastSelectedPlanValues);
      });
      CollectionHelper.filterInPlace(
        this.itemGroup.items,
        (e, id) =>
          event.subProjectId != null
            ? e.type === 'Task' || e.id !== id[0]
            : e.type !== 'Task' || e.id !== id[0],
        event.subProjectId != null ? event.subProjectId : event.immoTaskId
      );
      ImmoMapHelper.reprocessGroupAppointmentInfo(this.itemGroup);
      this.itemGroup.markerColor = ImmoMapHelper.calculateGroupMarker(
        this.itemGroup
      );
    }
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
